import { useEffect, useState } from "react";

import type { WireSeason } from "../api/types.js";
import { seasonLabel } from "../domain/format.js";
import { useDebouncedValue } from "../hooks/useDebouncedValue.js";
import type { PickedUpFilter } from "./useListFilters.js";

const PICKED_UP_OPTIONS: Array<{ value: PickedUpFilter; label: string }> = [
  // Same three radios, same order, as MainWindow.xaml:65-67.
  { value: "all", label: "Vse" },
  { value: "yes", label: "Dvignjeno" },
  { value: "no", label: "V skladišču" },
];

export interface FiltersBarProps {
  seasons: WireSeason[];
  seasonId: string | null;
  onSeasonChange: (id: string) => void;
  pickedUp: PickedUpFilter;
  onPickedUpChange: (value: PickedUpFilter) => void;
  search: string;
  onSearchChange: (value: string) => void;
  onNewItem: () => void;
  onCloseSeason: () => void;
  canCloseSeason: boolean;
}

export function FiltersBar({
  seasons,
  seasonId,
  onSeasonChange,
  pickedUp,
  onPickedUpChange,
  search,
  onSearchChange,
  onNewItem,
  onCloseSeason,
  canCloseSeason,
}: FiltersBarProps) {
  return (
    <div className="filters-bar">
      <div className="filters-bar-field">
        <label htmlFor="season-select">Sezona</label>
        <select
          id="season-select"
          value={seasonId ?? ""}
          onChange={(e) => onSeasonChange(e.target.value)}
          disabled={seasons.length === 0}
        >
          {seasons.length === 0 && <option value="">Ni sezon</option>}
          {seasons.map((season) => (
            <option key={season.id} value={season.id}>
              {seasonLabel(season)}
            </option>
          ))}
        </select>
      </div>

      <fieldset className="filters-bar-field filters-bar-radios">
        <legend>Stanje</legend>
        <div className="filters-bar-radio-group">
          {PICKED_UP_OPTIONS.map((opt) => (
            <label key={opt.value} className="filters-bar-radio">
              <input
                type="radio"
                name="pickedUp"
                value={opt.value}
                checked={pickedUp === opt.value}
                onChange={() => onPickedUpChange(opt.value)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="filters-bar-field filters-bar-search">
        <label htmlFor="search-input">Iskanje</label>
        <SearchInput value={search} onChange={onSearchChange} />
      </div>

      <div className="filters-bar-actions">
        <button type="button" onClick={onCloseSeason} disabled={!canCloseSeason} className="filters-bar-secondary">
          Nova sezona
        </button>
        <button type="button" onClick={onNewItem} className="filters-bar-primary">
          Nov vnos
        </button>
      </div>
    </div>
  );
}

/** Debounces committing to the URL (which triggers a refetch) while keeping every keystroke visible immediately. */
function SearchInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [local, setLocal] = useState(value);

  useEffect(() => {
    setLocal(value);
    // Only re-sync from the outside (e.g. browser back/forward) — not meant to run on every local keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const debounced = useDebouncedValue(local, 300);

  useEffect(() => {
    if (debounced !== value) onChange(debounced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  return (
    <input
      id="search-input"
      type="search"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      placeholder="Priimek, ime ali reg. št."
    />
  );
}
